'use client';

import { UiIcon } from '@/components/ui/UiIcon';
import { cn } from '@/lib/utils/tw-merge';
import { useDictionary } from '@/components/providers';
import { setTheme, useTheme, type Theme } from './theme.store';

const OPTIONS: { value: Theme; label: string; icon: 'sun' | 'moon' }[] = [
    { value: 'light', label: 'Light Mode', icon: 'sun' },
    { value: 'dark', label: 'Dark Mode', icon: 'moon' },
];

/** Segmented light/dark control (off-canvas, narrow viewports). A track with an
 *  elevated active pill; shares the theme store with ThemeToggle. Sizing/colors
 *  per the PABO design (bg-surface-2 track, bg-page active pill + soft shadow). */
export function ThemeSwitch({ className }: { className?: string }) {
    const theme = useTheme();
    const { a11y } = useDictionary();

    return (
        <div
            role="radiogroup"
            aria-label={a11y.colorScheme}
            className={cn('flex w-full gap-1 rounded-[10px] bg-surface-2 p-[3px]', className)}
        >
            {OPTIONS.map((option) => {
                const active = theme === option.value;
                return (
                    <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setTheme(option.value)}
                        className={cn(
                            'inline-flex min-h-[34px] flex-1 items-center justify-center gap-2 rounded-lg px-2.5 py-1.5',
                            'cursor-pointer text-sm font-medium [transition:var(--transition-button)]',
                            active
                                ? 'bg-page text-title shadow-[0_1px_2px_rgba(0,0,0,0.12)]'
                                : 'text-copy-muted hover:text-title',
                        )}
                    >
                        <UiIcon name={option.icon} size={16} />
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}
